import "@googlemaps/js-api-loader";

import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import GoogleMapReact from "google-map-react";

import "./index.css";

import { debounce } from "lodash";

import { camelToStartCase, intToTime, log } from "@repo/helpers";

import { Slider } from "../../basic/slider";
import { Color, getFilterValuesForHex } from "./color";

interface TimeSliderSettings {
	enabled: boolean;
	min: number;
	max: number;
}

interface Label {
	[key: string]: string;
}

interface Link {
	href: string;
	label: string;
	value: string | number;
}

export interface Marker {
	id: number | string;
	group?: string;
	datetime: Date;
	lat: number;
	lon: number;
	label: Label[];
	image: string;
	tooltip?: string;
	links?: Link[];
}

export interface Bounds {
	northeast: [number, number];
	southwest: [number, number];
}

export interface LatLng {
	lat: number;
	lng: number;
}

interface MapChartProps {
	id: string;
	title: string;
	data: Marker[];
	type: "map" | "heatmap";
	mapType?: "roadmap" | "satellite";
	realtime?: boolean;
	timeSlider?: TimeSliderSettings;
	height?: number | string;
	loading?: boolean;
	onBoundsChange?: (bounds: Bounds) => void;
	dontUpdateBounds?: boolean;
	forcedCenter?: LatLng;
	lineData?: Marker[];
	showLines?: boolean;
}

interface CustomMarker extends google.maps.marker.AdvancedMarkerElement {
	key: string;
}

const marks = {
	0: intToTime(0),
	6: intToTime(6),
	12: intToTime(12),
	18: intToTime(18),
	24: intToTime(24),
};

export const MapChart: FC<MapChartProps> = ({
	id,
	title,
	data,
	type,
	mapType,
	realtime,
	timeSlider,
	height = 372,
	loading,
	onBoundsChange,
	dontUpdateBounds,
	forcedCenter,
	lineData,
	showLines = false,
}) => {
	const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
	const [displayMarkers, setDisplayMarkers] = useState<boolean>(false);
	const [markers, setMarkers] = useState<CustomMarker[]>([]);
	const [sliderValue, setSliderValue] = useState<number[]>([0, 24]);
	const [initialized, setInitialized] = useState<boolean>(false);
	const [lines, setLines] = useState<google.maps.Polyline[]>([]);
	const [infoWindow, setInfoWindow] = useState<boolean>(false);

	useEffect(() => {
		if (timeSlider?.enabled) {
			setSliderValue([timeSlider.min, timeSlider.max]);
		}
	}, [timeSlider]);

	const filteredData = useMemo(() => {
		return data
			.filter((item) => item.lat && item.lon)
			.filter((item) => !item.datetime || item.datetime.getHours() >= (sliderValue?.at(0) ?? 0))
			.filter((item) => !item.datetime || item.datetime.getHours() <= (sliderValue?.at(1) ?? 24));
	}, [data, sliderValue]);

	const filteredLineData = useMemo(() => {
		return (
			lineData
				?.filter((item) => item.lat && item.lon)
				.filter((item) => !item.datetime || item.datetime.getHours() >= (sliderValue?.at(0) ?? 0))
				.filter((item) => !item.datetime || item.datetime.getHours() <= (sliderValue?.at(1) ?? 24)) ?? []
		);
	}, [data, sliderValue]);

	useEffect(() => {
		if (initialized) return;
		if (markers?.length) setInitialized(true);
	}, [initialized, markers]);

	const center = USA_CENTER;

	const computedBounds = useMemo(() => {
		if (!window.google?.maps) return undefined;
		//If some data is loading, but we've already initialized, don't reset bounds
		if (!markers?.length || loading) {
			return initialized ? undefined : USA_CENTER_BOUNDS();
		}
		const b = new window.google.maps.LatLngBounds();
		markers.forEach((item) => {
			b.extend(item.position);
		});

		return b;
	}, [markers, initialized, loading]);

	const onGoogleApiLoaded = ({ map }: { map: google.maps.Map }) => {
		setMapRef(map);
		setDisplayMarkers(true);
	};

	const heatmapData = useMemo(() => {
		return {
			positions: filteredData.map((item) => ({
				lat: item.lat,
				lng: item.lon,
				weight: 100,
			})),
			options: {},
		};
	}, [filteredData]);

	useEffect(() => {
		if (!mapRef) return;
		if (!computedBounds) return;
		if (dontUpdateBounds) return;

		mapRef.fitBounds(computedBounds);
	}, [computedBounds, mapRef, dontUpdateBounds]);

	useEffect(() => {
		if (!displayMarkers) return;
		if (!mapRef) return;

		if (!filteredData?.length) {
			markers.forEach((marker) => (marker.map = null));
			setMarkers([]);
			return;
		}
		if (!realtime) fullUpdateMarker().catch((e) => log(e, "error"));
		else pushRealTimeMarkers().catch((e) => log(e, "error"));
	}, [filteredData, type, realtime, displayMarkers, mapRef]);

	async function fullUpdateMarker() {
		const { InfoWindow } = (await google.maps.importLibrary("maps")) as google.maps.MapsLibrary;
		const { AdvancedMarkerElement } = (await google.maps.importLibrary("marker")) as google.maps.MarkerLibrary;

		const infoWindow = new InfoWindow();

		const currentMarkerMap = new Map<string | number, CustomMarker>(markers.map((marker) => [marker.key, marker]));

		const filteredDataMap = new Map(filteredData.map((item) => [item.id, item]));

		// Remove markers that aren't in filtered data
		for (const marker of currentMarkerMap.values()) {
			if (!filteredDataMap.has(marker.key)) {
				marker.map = null;
				currentMarkerMap.delete(marker.key);
			}
		}

		// Update existing markers and add new ones
		for (const item of filteredDataMap.values()) {
			if (!currentMarkerMap.has(item.id)) {
				const newMarker: CustomMarker = createMarker(item, item.id, infoWindow, AdvancedMarkerElement);
				currentMarkerMap.set(item.id, newMarker);
			} else {
				const marker = currentMarkerMap.get(item.id);
				if (marker) {
					// might still need to update color
					marker.position = { lat: item.lat, lng: item.lon };
				}
			}
		}

		// Update markers state with current set
		setMarkers(Array.from(currentMarkerMap.values()));
	}

	async function pushRealTimeMarkers() {
		const { InfoWindow } = (await google.maps.importLibrary("maps")) as google.maps.MapsLibrary;
		const { AdvancedMarkerElement } = (await google.maps.importLibrary("marker")) as google.maps.MarkerLibrary;

		const infoWindow = new InfoWindow();
		const existingMarkerMap = new Map<string | number, CustomMarker>(markers.map((marker) => [marker.key, marker]));

		const currentMarkerMap = new Map<string | number, CustomMarker>(
			[...markers].map((marker) => [marker.key, marker]),
		);

		const filteredDataMap = new Map(filteredData.map((item) => [item.id, item]));

		// Create keyed array of filtered data
		for (const item of filteredDataMap.values()) {
			const existingMarker = existingMarkerMap.get(item.id);

			// Check if marker exists and needs color update
			if (existingMarker) {
				const currentImage = (existingMarker.content as HTMLImageElement).src;
				const newImage = item.image;

				if (currentImage !== newImage) {
					const flagImage = existingMarker.content as HTMLImageElement;
					flagImage.src = newImage;
				}

				// Update position if changed
				const position = existingMarker.position as google.maps.LatLng;
				// @ts-ignore
				if (position.lat !== item.lat || position.lng !== item.lon) {
					existingMarker.position = { lat: item.lat, lng: item.lon };
				}

				existingMarkerMap.delete(item.id); // Remove from map to track which ones are new
				return;
			}

			const newMarker = createMarker(item, item.id, infoWindow, AdvancedMarkerElement);
			currentMarkerMap.set(item.id, newMarker);
		}
		setMarkers(Array.from(currentMarkerMap.values()));
	}

	function createMarker(
		item: Marker,
		key: string | number,
		infoWindow: google.maps.InfoWindow,
		AdvancedMarkerElement: any,
	) {
		// Create new marker
		const flagImage = document.createElement("img");
		flagImage.src = item.image;
		flagImage.width = 32;
		flagImage.height = 32;
		flagImage.title = item.tooltip;

		if (item.group) {
			flagImage.classList.add("marker-image");
			const color = Color.fromString(item.group);
			const filter = getFilterValuesForHex(color.toHex());

			flagImage.style.setProperty("--invert", `${filter.values.invert.toFixed(2)}%`);
			flagImage.style.setProperty("--sepia", `${filter.values.sepia.toFixed(2)}%`);
			flagImage.style.setProperty("--saturate", `${filter.values.saturate.toFixed(2)}%`);
			flagImage.style.setProperty("--hue-rotate", `${(filter.values.hueRotate * 3.6).toFixed(2)}deg`);
			flagImage.style.setProperty("--brightness", `${filter.values.brightness.toFixed(2)}%`);
			flagImage.style.setProperty("--contrast", `${filter.values.contrast.toFixed(2)}%`);
		}

		const marker = new AdvancedMarkerElement({
			id: key,
			position: { lat: item.lat, lng: item.lon },
			map: mapRef,
			content: flagImage,
			gmpClickable: true,
		});

		// Store key for future reference
		marker.key = key;

		// Add animation for new markers
		const content = marker.content as HTMLElement;
		content.style.opacity = "0";
		content.classList.add("drop");
		content.addEventListener("animationend", () => {
			content.classList.remove("drop");
			content.style.opacity = "1";
		});

		// Add marker click handler (rest of the click handler code remains the same)
		marker.addListener("click", () => {
			const currentBounds = mapRef?.getBounds();
			const currentZoom = mapRef?.getZoom();

			mapRef?.setMapTypeId("satellite");
			mapRef?.setCenter({ lat: item.lat, lng: item.lon });
			mapRef?.setZoom(19);

			infoWindow.close();
			infoWindow.setContent(
				`<div style="color: black">
					<dl style="display: grid; grid-template-columns: 1fr 2fr; gap: 4px; padding-bottom: 4px">
						${
							item.links
								?.map((l) => {
									return `<dt style="font-weight: bold">${l.label}:</dt><dd><a href="${l.href}" target="_blank" style="color: blue; text-decoration: underline">${l.value}</a></dd>`;
								})
								.join("") ?? ""
						}
						${item.label
							.map((l) => {
								return Object.keys(l)
									.map((k) => {
										return `<dt style="font-weight: bold">${camelToStartCase(k)}:</dt><dd>${l[k]}</dd>`;
									})
									.join("");
							})
							.join("")}
					</dl>
					<div style="display: flex; flex-direction: column; align-items: start; gap: 4px">
						<button id="openStreetView" style="color: blue">Open Street View</button>
					</div>
				</div>`,
			);

			infoWindow.open(mapRef, marker);
			setInfoWindow(true);

			infoWindow.addListener("closeclick", () => {
				mapRef?.setMapTypeId(mapType ?? "roadmap");

				if (currentBounds) {
					mapRef?.fitBounds(currentBounds);
					mapRef?.setZoom(currentZoom);
				} else {
					mapRef?.setCenter({ lat: item.lat, lng: item.lon });
					mapRef?.setZoom(11);
				}

				setInfoWindow(false);
			});

			infoWindow.addListener("domready", () => {
				const openStreetViewButton = document.getElementById("openStreetView");
				if (!openStreetViewButton) return;
				openStreetViewButton.addEventListener("click", () => openStreetView(item.lat, item.lon));
			});

			function openStreetView(lat: number, lng: number) {
				if (!mapRef) return;

				const panorama: google.maps.StreetViewPanorama = mapRef?.getStreetView();

				panorama.setPosition({ lat, lng });
				panorama.setVisible(true);

				const size = new google.maps.Size(96, 96);
				const image = {
					url: flagImage.src,
					size,
					scaledSize: size,
				} as google.maps.Icon;

				const marker = new google.maps.Marker({
					position: { lat, lng },
					title: "",
					icon: image,
				});

				marker.setMap(panorama);

				mapRef.setStreetView(panorama);

				panorama.addListener("closeclick", () => {
					marker.setMap(null);
				});
			}
		});
		return marker;
	}

	const viewport = mapRef?.getBounds();

	useEffect(() => {
		const debouncedBoundsChange = debounce((viewport) => {
			if (viewport != null && onBoundsChange != null) {
				const northEast = viewport.getNorthEast();
				const southWest = viewport.getSouthWest();

				onBoundsChange({
					northeast: [northEast.lat(), northEast.lng()],
					southwest: [southWest.lat(), southWest.lng()],
				});
			}
		}, 3000);

		if (infoWindow) return;
		debouncedBoundsChange(viewport);

		return () => {
			debouncedBoundsChange.cancel();
		};
	}, [viewport, onBoundsChange]);

	useEffect(() => {
		if (forcedCenter) {
			mapRef.setCenter(forcedCenter);
			mapRef.setZoom(9);
		}
	}, [forcedCenter, mapRef]);

	const clearLines = (lines: google.maps.Polyline[]) => {
		lines.forEach((line) => {
			line.setMap(null);
		});
		setLines([]);
	};

	useEffect(() => {
		if (showLines) return;
		if (lines.length > 0) clearLines(lines);
	}, [showLines, lines]);

	useEffect(() => {
		if (filteredLineData.length < 2) return;
		if (!showLines) return;
		if (!mapRef) return;

		clearLines(lines);

		const markersDict = new Map<string, { lat: number; lng: number; color: string }[]>();
		filteredLineData.forEach((marker) => {
			if (!markersDict.has(marker.group)) {
				markersDict.set(marker.group, []);
			}
			markersDict
				.get(marker.group)
				?.push({ lat: marker.lat, lng: marker.lon, color: Color.fromString(marker.group).toHex() });
		});

		const newLines = [];

		for (const key of markersDict.keys()) {
			const color = markersDict.get(key)[0].color;

			const polyline = new google.maps.Polyline({
				path: markersDict.get(key),
				geodesic: true,
				strokeColor: color,
				strokeOpacity: 1,
				strokeWeight: 2,
			});

			polyline.setMap(mapRef);

			newLines.push(polyline);
		}

		setLines(newLines);
	}, [filteredLineData, showLines, mapRef]);

	return (
		<div id={id} className="">
			{timeSlider?.enabled ? (
				<Slider
					className="tw-w-full tw-px-4 tw-pb-6"
					marks={marks}
					min={timeSlider.min}
					max={timeSlider.max}
					step={1}
					defaultValue={[timeSlider.min, timeSlider.max]}
					value={sliderValue}
					onChange={setSliderValue}
					draggableTrack
				/>
			) : null}
			<div style={{ height }}>
				<GoogleMapReact
					bootstrapURLKeys={{
						// TODO: don't hard code this
						key: "AIzaSyDcKVNzjWpSbwSvi8lig6-b5m9iXn7I1zI",
						libraries: ["visualization", "marker"],
					}}
					options={{
						minZoom: 3,
						mapTypeId: mapType ?? "roadmap",
						mapTypeControl: true,
						scaleControl: false,
						streetViewControl: false,
						rotateControl: true,
						fullscreenControl: true,
						zoomControl: true,
						clickableIcons: false,
						mapId: "4504f8b37365c3d0",
					}}
					defaultCenter={center}
					defaultZoom={3}
					heatmapLibrary={type === "heatmap"}
					heatmap={type === "heatmap" ? heatmapData : undefined}
					onGoogleApiLoaded={onGoogleApiLoaded}
					yesIWantToUseGoogleMapApiInternals
					shouldUnregisterMapOnUnmount
				/>
				<LoadingOverlay />
			</div>
		</div>
	);

	function LoadingOverlay() {
		if (!loading) return null;
		return (
			<div className="tw-absolute tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-white/50">
				<div className="tw-h-8 tw-w-8 tw-animate-spin tw-rounded-full tw-border-4 tw-border-blue-600 tw-border-t-transparent" />
			</div>
		);
	}
};

/** Geographic center of the USA */
const USA_CENTER = { lat: 39.8282, lng: -98.5795 };
const USA_CENTER_BOUNDS = () =>
	new google.maps.LatLngBounds().extend({ lat: 24.5205, lng: -125.0 }).extend({ lat: 49.3844, lng: -66.9346 });
